import React, { useId, useRef, useState } from 'react';
import { HelpCircle, GripHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';

const STOPS = [
  { fill: '#D7E0D4', ink: '#1C1917' },
  { fill: '#C5D4B8', ink: '#1C1917' },
  { fill: '#E4D7A3', ink: '#1C1917' },
  { fill: '#E8C56A', ink: '#1C1917' },
  { fill: '#E0A83A', ink: '#1C1917' },
  { fill: '#D9891A', ink: '#1C1917' },
  { fill: '#B45309', ink: '#FAF7F1' },
  { fill: '#C2410C', ink: '#FAF7F1' },
  { fill: '#B91C1C', ink: '#FAF7F1' },
  { fill: '#7F1D1D', ink: '#FAF7F1' },
];

function valueFromClientX(clientX, el) {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) return 1;
  const idx = Math.floor(((clientX - rect.left) / rect.width) * 10);
  return Math.min(10, Math.max(1, idx + 1));
}

export default function RpeHeatSlider({ value, onChange, label = 'RPE', he = false }) {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const lastRef = useRef(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const helpId = useId();
  const rpe = Math.min(10, Math.max(0, parseInt(value, 10) || 0));
  lastRef.current = rpe;

  const commitFromEvent = (event) => {
    const next = valueFromClientX(event.clientX, trackRef.current);
    if (next !== lastRef.current) {
      lastRef.current = next;
      onChange(next);
    }
  };

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    draggingRef.current = true;
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    commitFromEvent(event);
  };

  const onPointerMove = (event) => {
    if (!draggingRef.current) return;
    commitFromEvent(event);
  };

  const onPointerUp = (event) => {
    draggingRef.current = false;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const onKeyDown = (event) => {
    const base = rpe || 1;
    let next = base;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = Math.min(10, base + 1);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = Math.max(1, base - 1);
    else if (event.key === 'Home') next = 1;
    else if (event.key === 'End') next = 10;
    else return;
    event.preventDefault();
    if (next !== rpe) onChange(next);
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="relative shrink-0 group/rpe">
        <div className="flex items-center gap-0.5">
          <span className="text-[11px] font-semibold text-ink-muted">{label}</span>
          <button
            type="button"
            aria-label={he ? 'מה זה RPE' : 'What is RPE'}
            aria-expanded={helpOpen}
            aria-describedby={helpId}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setHelpOpen((open) => !open);
            }}
            onBlur={() => setHelpOpen(false)}
            className="min-h-6 min-w-6 inline-flex items-center justify-center rounded-full text-ink-muted hover:text-ink dark:hover:text-ink-dark cursor-help outline-none focus-visible:ring-2 focus-visible:ring-forest"
          >
            <HelpCircle size={13} strokeWidth={2.25} />
          </button>
        </div>
        <div
          id={helpId}
          role="tooltip"
          className={`absolute start-0 top-full z-30 mt-1 w-[15.5rem] p-2.5 rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark text-[11px] leading-snug text-ink dark:text-ink-dark transition-opacity duration-120 ${
            helpOpen
              ? 'opacity-100'
              : 'opacity-0 pointer-events-none group-hover/rpe:opacity-100 group-focus-within/rpe:opacity-100'
          }`}
        >
          <p className="font-semibold">
            {he ? 'דירוג מאמץ סובייקטיבי (1–10)' : 'Rate of Perceived Exertion (1–10)'}
          </p>
          <p className="mt-1 text-ink-muted">
            {he
              ? '1 קל מאוד, 10 מאמץ מרבי. בחרו את המספר שבו המתאמן אמור לסיים את הסט.'
              : '1 is very easy, 10 is all-out. Pick the number the client should finish the set at.'}
          </p>
          <p className="mt-1.5 text-ink-muted tabular-nums">
            {he ? '1–3 קל · 4–6 עבודה · 7–8 קשה · 9–10 מקס׳' : '1–3 easy · 4–6 working · 7–8 hard · 9–10 max'}
          </p>
        </div>
      </div>
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={1}
        aria-valuemax={10}
        aria-valuenow={rpe || undefined}
        aria-valuetext={rpe ? `${label} ${rpe}` : `${label} unset`}
        dir="ltr"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        className={`relative flex-1 min-w-0 min-h-12 select-none outline-none focus-visible:ring-2 focus-visible:ring-forest touch-none ${
          dragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
      >
        <div className="pointer-events-none absolute inset-y-0 start-0 z-10 flex items-center ps-0.5 text-ink-muted">
          <ChevronLeft size={14} strokeWidth={2.5} />
        </div>
        <div className="pointer-events-none absolute inset-y-0 end-0 z-10 flex items-center pe-0.5 text-ink-muted">
          <ChevronRight size={14} strokeWidth={2.5} />
        </div>
        <div className={`flex min-h-12 rounded-md border overflow-hidden transition-colors duration-120 ${
          dragging ? 'border-forest' : 'border-line dark:border-line-dark hover:border-forest/60'
        }`}>
        {STOPS.map((stop, i) => {
          const n = i + 1;
          const active = rpe > 0 && n <= rpe;
          const selected = n === rpe;
          return (
            <div
              key={n}
              aria-hidden="true"
              className={`relative flex-1 min-w-0 min-h-12 flex items-center justify-center text-[11px] tabular-nums transition-opacity duration-120 ${
                selected ? 'font-semibold' : 'font-medium'
              } ${i > 0 ? 'border-s border-line/70 dark:border-line-dark/70' : ''}`}
              style={{
                backgroundColor: stop.fill,
                color: stop.ink,
                opacity: active ? 1 : 0.28,
              }}
            >
              {!selected && n}
            </div>
          );
        })}
        </div>
        {rpe > 0 && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute top-1/2 z-20 flex h-12 w-[10%] min-w-8 -translate-y-1/2 flex-col items-center justify-center rounded-md border-2 transition-[left] duration-120 ${
              dragging ? 'border-forest' : 'border-ink dark:border-ink-dark'
            }`}
            style={{
              left: `${(rpe - 1) * 10}%`,
              backgroundColor: STOPS[rpe - 1].fill,
              color: STOPS[rpe - 1].ink,
            }}
          >
            <GripHorizontal size={12} strokeWidth={2.5} />
            <span className="text-[12px] font-semibold tabular-nums leading-none">{rpe}</span>
          </div>
        )}
      </div>
      <span className="shrink-0 w-8 text-end text-[10px] font-medium text-ink-muted leading-tight">
        {he ? 'גרור' : 'drag'}
      </span>
    </div>
  );
}
